import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../utils/translator.helper';
import { AttributionActor } from '../types';
import { renderIconSet } from '../types/iconSet';
import { GzIconWebComponent } from '../../../ui/gzIcon';
import { uiFlagIconSet } from '../types/flagIconSet';
import { AttributionData } from '../../xdrOverview/types';
import { InProductTrialTypeEnum } from '@nirvana/shared/dtos/types/in-product-trials';
import { IntelliZoneIntegrationData } from '../../../utils/xdrOverview/intellizone/intellizone.types';
import { Rights } from '@nirvana/shared/constants';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { FontSizes } from "../../../types/common/types";

@customElement('gz-suspected-actors-card-component')
export class SuspectedActorsCardWebComponent extends LitElement {
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  sectionTitle = translateFn('Suspected actors');

  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  companyId = null;

  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  attribution: AttributionData;

  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  actors: AttributionActor[];

  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  errorState = false;

  static get styles() {
    return styles;
  }

  private handleIZPivot() {
    // Navigate to product trials page
    NavigationHelper.navigateToProductsHub(InProductTrialTypeEnum.ThreatIntel);
  }

  render(): TemplateResult {
    if (this.attribution) {
      this.actors = this.attribution.actors;
    }

    let contentTemplate: TemplateResult<1>;
    if (this.errorState) {
      contentTemplate = html` ${this.renderErrorState()} `;
    } else if (!this.actors || !this.actors.length) {
      contentTemplate = html` ${this.renderEmptyState()} `;
    } else if (this.actors.length === 1) {
      const item = this.actors[0];
      contentTemplate = html`
        ${this.renderTitleContent(item)}
        <gz-actor-card .actor="${item}"
                       .companyId="${this.companyId}"
                       .intelliZoneIntegrationData="${
                         {
                           canPivotToIntelliZone: this.attribution.canPivotToIntelliZone,
                           hasAccessToIZProductTrial: this.attribution.hasAccessToIZProductTrial,
                           intellizoneUrl: this.attribution.intellizoneUrl
                         } as IntelliZoneIntegrationData
                       }"
        ></gz-actor-card>
      `;
    } else {
      contentTemplate = html` ${this.buildAccordionContent()} `;
    }
    return html` ${this.renderTitle()} ${contentTemplate}`;
  }

  private renderTitle(): TemplateResult<1> {
    return html`
      <gz-title class="title text-uppercase pb-2" fontweight="500" fontsize="16">${this.sectionTitle}</gz-title>`;
  }

  private buildAccordionContent(): TemplateResult<1>[] {
    return this.actors.map((item, index) => this.renderAccordionItem(item, index));
  }

  private renderAccordionItem(item: AttributionActor, index: number): TemplateResult<1> {
    return html`
      <details name="suspectedActorsAccordionGroup" ?open=${index === 0} actorId="${item.id}">
        <summary>${this.renderTitleContent(item)}</summary>
        <gz-actor-card .actor="${item}"
                       .companyId="${this.companyId}"
                       .intelliZoneIntegrationData="${
                          {
                            canPivotToIntelliZone: this.attribution.canPivotToIntelliZone,
                            hasAccessToIZProductTrial: this.attribution.hasAccessToIZProductTrial,
                            intellizoneUrl: this.attribution.intellizoneUrl
                          } as IntelliZoneIntegrationData
                       }"
        ></gz-actor-card>
      </details>
    `;
  }

  private renderTitleContent(item: AttributionActor): TemplateResult<1> {
    return html`
      <div class="d-flex w-full header-content">
        ${renderIconSet.createIconSet(this.getActorIconName(item.name))} ${this.renderNameAndConfidence(item)}
        ${this.renderIZPivotButton(item)}
      </div>
    `;
  }

  private renderIZPivotButton(item: AttributionActor): TemplateResult<1> {

    if (this.attribution.canPivotToIntelliZone) {
      const tooltipText = translateFn('Search in IntelliZone');
      return html`
        <gz-tooltip
          tooltipText="${tooltipText}"
          class="d-flex align-items-center"
        >
          ${this.renderIZButton(this.openIntelliZoneActorPage, false, item)}
        </gz-tooltip>
      `;
    } else {
      let tooltipText = '';
      if (!this.attribution.hasAccessToIZProductTrial) {
        tooltipText = !Application.Main.hasAnyRights([Rights.RIGHT_COMPANY_MANAGER])
          ? translateFn('Your company requires an integration with IntelliZone to access this feature. Please contact a user with Company Administrator rights.')
          : translateFn('Your company does not have an active integration with IntelliZone. Contact your Account Manager to request access.');
      }
      return html`
        <gz-tooltip
          tooltipText="${tooltipText}"
          class="d-flex align-items-center"
        >
          ${this.renderIZButton(this.handleIZPivot, !this.attribution.hasAccessToIZProductTrial)}
        </gz-tooltip>
      `;
    }

  }

  private openIntelliZoneActorPage(actorUrl) {
    window.open(actorUrl);
  }

  private renderIZButton(callback, disabled = false, actor: AttributionActor|null  = null): TemplateResult<1> {
    const actorUrl = actor ? this.attribution.intellizoneUrl + "/actors/" + actor?.intellizoneId : null;
    return html`
      <gz-button
        type="quiet"
        color="secondary"
        class="d-flex align-items-center justify-center w-3"
        @click="${this.handleClickIZButton(callback, actorUrl)}"
        ?disabled="${disabled}"
      >
        ${renderIconSet.shieldIcon(this.attribution.canPivotToIntelliZone)}
      </gz-button>
    `;
  }

private handleClickIZButton(callback, actorUrl) {
    // If actorUrl is not null, send it as parameter to the callback function, else use the callback function without parameters
    return actorUrl ? () => callback(actorUrl) : callback;
}

  /**
   * Format actor names for icons
   * @param {string} name
   * @returns {string}
   * @private
   */
  private getActorIconName(name: string): string {
    return `${name.toLowerCase().replace(/\s/g, '-')}-icon`;
  }

  private renderNameAndConfidence(item: AttributionActor): TemplateResult<1> {
    let country;
    if (item.country && item.country[0]) {
      country = item.country[0];
    }
    return html`
      <div class="d-flex flex-column flex-1 title-text-container">
        <div class="name-container d-flex flex-row align-items-center flex-1 pr-1">
          ${this.renderTooltipAndIcon(item.name, country)}
        </div>
        ${this.renderConfidence(item.confidence)}
      </div>
    `;
  }

  private renderTooltipAndIcon(text: string, flag: string): TemplateResult<1> {
    return html`
      <gz-tooltip tooltipText="${text}" isEllipsis>
        <gz-text class="d-inline" fontSize="14" fontWeight="500">${text}</gz-text>
      </gz-tooltip>
      ${flag ? this.renderIconFlag(flag) : ''}
    `;
  }

  private renderIconFlag(iconName: string): TemplateResult {
    const gzIcon = new GzIconWebComponent();
    gzIcon.setCustomIconSet(uiFlagIconSet);
    gzIcon.classList.add('pl-1');
    gzIcon.name = iconName;
    return html`${gzIcon}`;
  }

  private renderConfidence(confidence: number): TemplateResult<1> {
    const confidenceText = translateFn('confidence');
    return html`
      <gz-tooltip tooltipText="${confidence}% ${confidenceText}" isEllipsis>
        <gz-text class="d-inline confidence" fontSize="14" fontWeight="300">${confidence}% ${confidenceText}</gz-text>
      </gz-tooltip>
    `;
  }

  private renderEmptyState(): TemplateResult {
    return html`
      <gz-image-repo>
        <gz-title class="image-repo-title" fontSize='${FontSizes.HEADING_4}' fontWeight="500"> ${translateFn('No actors detected')}</gz-title>
        <gz-text class="image-repo-text" fontSize="13" fontWeight="100">
          ${translateFn('No threat actors were detected for this incident.')}
        </gz-text>
      </gz-image-repo>`;
  }

  private renderErrorState(): TemplateResult {
    // TODO add refresh button here
    return html`
      <gz-image-repo>
        <gz-title class="image-repo-title" fontSize='${FontSizes.HEADING_4}' fontWeight="500"> ${translateFn('Unable to retrieve actor data')}</gz-title>
        <gz-text class="image-repo-text" fontSize="13" fontWeight="100">
          ${translateFn('Something went wrong while processing actor data. Please refresh the page and try again. If the error persists, contact Support.')}
        </gz-text>
      </gz-image-repo>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-suspected-actors-card-component': SuspectedActorsCardWebComponent;
  }
}
